import { useEffect, useState } from 'preact/hooks';
import { useModel, model } from './model.ts';
import type { Syncer } from './sync.ts';
import { Review } from './views/Review.tsx';
import { Browse } from './views/Browse.tsx';
import { Edit } from './views/Edit.tsx';
import { Settings } from './views/Settings.tsx';
import { Decks } from './views/Decks.tsx';
import { useToast, dismissToast } from './toast.ts';
import { onUpdateReady, applyUpdate } from './sw-client.ts';

type Tab = 'review' | 'browse' | 'edit' | 'decks' | 'settings';

export function App({ syncer }: { syncer: Syncer }) {
  const m = useModel();
  const [tab, setTab] = useState<Tab>('review');
  const [editId, setEditId] = useState<string | null>(null);
  const [browseDeck, setBrowseDeck] = useState<string | null>(null);
  const [updateReady, setUpdateReady] = useState(false);
  const t = useToast();

  // Signed out, there is nothing to interrupt, so take an update at once; this
  // is also the only way a signed-out device could ever reach a fixed sign-in.
  useEffect(
    () =>
      onUpdateReady(() => {
        if (!model.token) applyUpdate();
        else setUpdateReady(true);
      }),
    [],
  );

  if (!m.ready) return <div class="center muted">Loading…</div>;
  if (!m.token) return <Login syncer={syncer} />;

  const dot =
    m.sync.status === 'syncing'
      ? 'syncing'
      : m.sync.status === 'error' || m.sync.status === 'signed-out'
        ? 'bad'
        : m.sync.status === 'offline'
          ? 'offline'
          : m.sync.pending
            ? 'pending'
            : 'ok';

  return (
    <div class="app">
      <header>
        <nav>
          {(
            [
              ['review', 'Study'],
              ['browse', 'Cards'],
              ['decks', 'Decks'],
              ['settings', 'Settings'],
            ] as const
          ).map(([k, label]) => (
            <button key={k} class={tab === k || (k === 'browse' && tab === 'edit') ? 'on' : ''} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </nav>
        <button
          class={`sync-dot ${dot}`}
          title={`${m.sync.status}${m.sync.pending ? `, ${m.sync.pending} pending` : ''}`}
          aria-label="Sync now"
          onClick={() => void syncer.run()}
        />
      </header>

      {updateReady && (
        <div class="banner">
          A new version is ready.{' '}
          <button class="link" onClick={applyUpdate}>
            Reload
          </button>
        </div>
      )}
      {m.sync.status === 'signed-out' && m.sync.error && (
        <div class="banner bad">
          {m.sync.error}{' '}
          <button class="link" onClick={() => void m.setToken(null)}>
            Sign in again
          </button>
        </div>
      )}

      <main>
        {tab === 'review' && <Review />}
        {tab === 'browse' && (
          <Browse
            deckId={browseDeck && m.decks.get(browseDeck) && !m.decks.get(browseDeck)!.deleted ? browseDeck : null}
            setDeckId={setBrowseDeck}
            onEdit={(id) => {
              setEditId(id);
              setTab('edit');
            }}
          />
        )}
        {tab === 'edit' && (
          <Edit key={editId ?? 'new'} id={editId} defaultDeck={browseDeck} onDone={() => setTab('browse')} />
        )}
        {tab === 'decks' && (
          <Decks
            onBrowse={(id) => {
              setBrowseDeck(id);
              setTab('browse');
            }}
          />
        )}
        {tab === 'settings' && <Settings syncer={syncer} />}
      </main>

      {t && (
        <div class="toast" key={t.id}>
          {t.text}
          {t.action && (
            <button
              class="link"
              onClick={() => {
                t.action!.run();
                dismissToast();
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Login({ syncer }: { syncer: Syncer }) {
  const m = useModel();
  const [manual, setManual] = useState(false);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${token.trim()}` } });
      if (res.status === 401) throw new Error('That token was not accepted.');
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      await m.setToken(token.trim());
      void navigator.storage?.persist?.();
      void syncer.run();
    } catch (err) {
      setError(err instanceof TypeError ? 'Could not reach the server.' : String((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  if (!manual) {
    return (
      <div class="login">
        <h1>Flashcards</h1>
        <p class="muted">Sign in once on each device. After that it works offline and syncs when it can.</p>
        {/* A real page load, not fetch: the server redirects to Pocket ID and back. */}
        <a class="button primary" href="/auth/login">
          Sign in with Pocket ID
        </a>
        <button class="link" onClick={() => setManual(true)}>
          Use an access token instead
        </button>
      </div>
    );
  }

  return (
    <form class="login" onSubmit={submit}>
      <h1>Flashcards</h1>
      <p class="muted">Paste an access token from the server's secrets file.</p>
      <input
        type="password"
        value={token}
        onInput={(e) => setToken(e.currentTarget.value)}
        placeholder="Access token"
        autocomplete="current-password"
        autoFocus
      />
      {error && <p class="error">{error}</p>}
      <button class="primary" disabled={busy || !token.trim()}>
        {busy ? 'Checking…' : 'Sign in'}
      </button>
    </form>
  );
}
